import { useEffect, useState } from 'react';

import type { FeedingDashboardOverview, FeedingPenOption } from '@/domain/feedingDashboard';

import type { PenSelection } from './components/PenFilterSelect/PenFilterSelect';

/**
 * The selection that still makes sense for a period's pens.
 *
 * Pens come and go with the period (Bioscopes rotate, pens are replaced), so a selection
 * made for one period can name pens another does not have. Those are dropped; when none
 * is left the selection returns to all pens rather than an empty page. A deliberate empty
 * selection stays empty. Returns the same value when nothing changes, so a caller can
 * compare by reference.
 */
export function reconcilePenSelection(
    selection: PenSelection,
    available: FeedingPenOption[]
): PenSelection {
    if (selection === 'all' || selection.length === 0) return selection;
    const inPeriod = new Set(available.map((pen) => pen.cageId));
    const kept = selection.filter((cageId) => inPeriod.has(cageId));
    if (kept.length === selection.length) return selection;
    return kept.length === 0 ? 'all' : kept;
}

/**
 * The pen selector's options, kept in step with each response.
 *
 * Options come from `availablePens` of every current response, the whole period whatever
 * is selected, and the selection is reconciled with them at the same time. A response
 * kept on screen while the next one loads is skipped: it describes the previous query.
 * With nothing selected there is no response, so the last options stay. So do they when
 * an older replica answers a subset request: its list is only the pens it was asked for,
 * and taking it would shrink the selector to the selection.
 */
export function usePenSelection(
    selection: PenSelection,
    setSelection: (selection: PenSelection) => void,
    response: Pick<FeedingDashboardOverview, 'availablePens' | 'availablePensDerived'> | undefined,
    isPreviousData: boolean
): FeedingPenOption[] {
    const [options, setOptions] = useState<FeedingPenOption[]>([]);
    useEffect(() => {
        if (!response || isPreviousData) return;
        if (response.availablePensDerived && selection !== 'all') return;
        setOptions(response.availablePens);
        const next = reconcilePenSelection(selection, response.availablePens);
        if (next !== selection) setSelection(next);
    }, [response, isPreviousData, selection, setSelection]);
    return options;
}
